/**
 * Resource allocation · the register: every stand, gate, check-in counter
 * and belt the engine plans over, and the form that changes one.
 *
 * The platform checks a change against the register and against the plan
 * (allocation/store.py change_resource; the mock the same way): a gate that
 * isn't on the register, a stand made too small for the flight placed on it,
 * or a stand taken off the register with flights still on it is refused with
 * the reason, shown under the form. A resource taken off the register stays
 * listed here, marked, so it can be brought back.
 */
import { useMemo, useState } from "react";
import { useAllocChangeResource, useAllocRegister } from "../../api/queries";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Segmented,
  Select,
  Skeleton,
} from "../../design/components";
import { KIND_LABEL, type Resource, type ResourceKind } from "../../domain/allocation";
import { t } from "../../i18n";
import { tx } from "../../i18n/computed";
import c from "./Allocation.module.css";
import a from "./Aodb.module.css";

type Row = Resource & { active: boolean };
const USES = ["pax", "cargo", "ferry", "positioning", "ga", "military", "maintenance", "other"] as const;
const USE_WORDS: Record<string, string> = {
  pax: "Passenger",
  cargo: "Cargo",
  ferry: "Ferry",
  positioning: "Positioning",
  ga: "General aviation",
  military: "Military",
  maintenance: "Maintenance",
  other: "Other",
};
const SIZES = ["A", "B", "C", "D", "E", "F"] as const;
const list = (s: string) =>
  s
    .split(/[,\s]+/)
    .map((x) => x.trim())
    .filter(Boolean);

export function RegisterTab() {
  const reg = useAllocRegister();
  const [kind, setKind] = useState<ResourceKind>("position");
  const [open, setOpen] = useState<string | null>(null);
  const rows = useMemo(() => (reg.data ?? []).filter((r) => r.kind === kind), [reg.data, kind]);
  if (reg.isPending) return <Skeleton rows={6} />;
  if (reg.isError) return <ErrorState error={reg.error} retry={() => void reg.refetch()} />;
  const editing = open ? (reg.data ?? []).find((r) => r.code === open) : undefined;
  return (
    <>
      <Card
        title="The register"
        sub={t("What the plan can use. Pick one to change it.")}
        testId="alloc-register"
        actions={
          <Segmented
            ariaLabel="Resource kind"
            value={kind}
            onChange={(k) => {
              setKind(k);
              setOpen(null);
            }}
            options={[
              { value: "position", label: KIND_LABEL.position },
              { value: "gate", label: KIND_LABEL.gate },
              { value: "counter", label: KIND_LABEL.counter },
              { value: "belt", label: KIND_LABEL.belt },
            ]}
          />
        }
      >
        {rows.length === 0 ? (
          <EmptyState>
            {t("No {kind} are in the register yet.", { kind: t(KIND_LABEL[kind]).toLowerCase() })}
          </EmptyState>
        ) : (
          <div className={c.scroll}>
            <table className={c.table}>
              <thead>
                <tr>
                  <th>{t("Code")}</th>
                  <th>{t("Name")}</th>
                  <th>{t("Group")}</th>
                  {(kind === "position" || kind === "belt") && <th>{t("Largest")}</th>}
                  {kind === "position" && <th>{t("Contact")}</th>}
                  {kind === "position" && <th>{t("Its gate")}</th>}
                  {kind === "position" && <th>{t("Uses")}</th>}
                  {kind === "gate" && <th>{t("Bus gate")}</th>}
                  <th>{t("On the register")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.code}
                    onClick={() => setOpen(r.code)}
                    data-testid={`reg-${r.code}`}
                    style={{ cursor: "pointer", opacity: r.active ? 1 : 0.55 }}
                  >
                    <td className={c.mono}>
                      <b>{r.code}</b>
                    </td>
                    <td>{r.name ?? "—"}</td>
                    <td>{r.group ?? "—"}</td>
                    {(kind === "position" || kind === "belt") && <td className={c.mono}>{r.maxSize ?? t("any")}</td>}
                    {kind === "position" && <td>{r.servedInPlace ? t("yes") : t("no")}</td>}
                    {kind === "position" && <td className={c.mono}>{r.links?.join(", ") || "—"}</td>}
                    {kind === "position" && <td>{r.uses?.map((u) => t(USE_WORDS[u] ?? u)).join(", ") || t("any")}</td>}
                    {kind === "gate" && <td>{r.bus ? t("yes") : t("no")}</td>}
                    <td>{r.active ? t("yes") : <b>{t("off the register")}</b>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {editing && <ResourceForm key={editing.code} r={editing} onClose={() => setOpen(null)} />}
    </>
  );
}

function ResourceForm({ r, onClose }: { r: Row; onClose: () => void }) {
  const save = useAllocChangeResource();
  const start = {
    name: r.name ?? "",
    group: r.group ?? "",
    seq: r.seq != null ? String(r.seq) : "",
    maxSize: r.maxSize ?? "",
    servedInPlace: Boolean(r.servedInPlace),
    links: (r.links ?? []).join(", "),
    excludes: (r.excludes ?? []).join(", "),
    uses: [...(r.uses ?? [])],
    bus: Boolean(r.bus),
    active: r.active,
  };
  const [v, setV] = useState(start);
  const [note, setNote] = useState("");
  const [bad, setBad] = useState<string | null>(null);
  const [done, setDone] = useState<string[] | null>(null);
  const set = (patch: Partial<typeof v>) => setV({ ...v, ...patch });
  const changes: Partial<Resource> & { active?: boolean } = {};
  if (v.name !== start.name) changes.name = v.name.trim() || undefined;
  if (v.group !== start.group) changes.group = v.group.trim() || undefined;
  if (v.seq !== start.seq) changes.seq = v.seq.trim() === "" ? undefined : Number(v.seq);
  if (v.maxSize !== start.maxSize) changes.maxSize = (v.maxSize || undefined) as Resource["maxSize"];
  if (v.servedInPlace !== start.servedInPlace) changes.servedInPlace = v.servedInPlace;
  if (v.links !== start.links) changes.links = list(v.links);
  if (v.excludes !== start.excludes) changes.excludes = list(v.excludes);
  if (v.uses.join() !== start.uses.join()) changes.uses = v.uses;
  if (v.bus !== start.bus) changes.bus = v.bus;
  if (v.active !== start.active) changes.active = v.active;
  /* a cleared field goes as null, so the platform clears it rather than leaving it */
  const wire = Object.fromEntries(Object.entries(changes).map(([k, x]) => [k, x === undefined ? null : x]));
  const any = Object.keys(wire).length > 0;
  const submit = () => {
    setBad(null);
    setDone(null);
    save.mutate(
      { code: r.code, changes: wire as Partial<Resource> & { active?: boolean }, note: note || undefined },
      {
        onSuccess: (out) => setDone(out.changed),
        onError: (e) => setBad(tx(e instanceof Error ? e.message : String(e))),
      },
    );
  };
  return (
    <Card
      title={t("Change {code}", { code: r.code })}
      sub={t(KIND_LABEL[r.kind])}
      testId="alloc-resource-form"
      actions={
        <Button size="sm" variant="ghost" onClick={onClose}>
          {t("Close")}
        </Button>
      }
    >
      <form
        className={a.form}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className={a.pair}>
          <Field label="Name">
            <Input value={v.name} onChange={(e) => set({ name: e.target.value })} data-testid="res-name" />
          </Field>
          <Field label="Group">
            <Input
              value={v.group}
              onChange={(e) => set({ group: e.target.value })}
              placeholder={t("pier, island or hall")}
            />
          </Field>
          <Field label="Order">
            <Input value={v.seq} onChange={(e) => set({ seq: e.target.value })} inputMode="numeric" />
          </Field>
          {(r.kind === "position" || r.kind === "belt") && (
            <Field label="Largest size">
              <Select value={v.maxSize} onChange={(e) => set({ maxSize: e.target.value })} data-testid="res-size">
                <option value="">{t("any")}</option>
                {SIZES.map((x) => (
                  <option key={x} value={x}>
                    {t("code {x}", { x })}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {r.kind === "position" && (
            <Field label="Its gate (codes)">
              <Input
                value={v.links}
                onChange={(e) => set({ links: e.target.value })}
                placeholder="12"
                data-testid="res-links"
              />
            </Field>
          )}
          {r.kind === "position" && (
            <Field label="Can't be used with (codes)">
              <Input value={v.excludes} onChange={(e) => set({ excludes: e.target.value })} placeholder="R2" />
            </Field>
          )}
        </div>
        {r.kind === "position" && (
          <label className={a.row}>
            <input
              type="checkbox"
              checked={v.servedInPlace}
              onChange={(e) => set({ servedInPlace: e.target.checked })}
              data-testid="res-contact"
            />
            {t("Contact stand: people walk through a bridge")}
          </label>
        )}
        {r.kind === "position" && (
          <fieldset className={a.row} style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className={a.dim}>{t("Uses (none ticked: any)")}</legend>
            {USES.map((u) => (
              <label key={u} className={a.row}>
                <input
                  type="checkbox"
                  checked={v.uses.includes(u)}
                  onChange={(e) => set({ uses: e.target.checked ? [...v.uses, u] : v.uses.filter((x) => x !== u) })}
                />
                {t(USE_WORDS[u])}
              </label>
            ))}
          </fieldset>
        )}
        {r.kind === "gate" && (
          <label className={a.row}>
            <input type="checkbox" checked={v.bus} onChange={(e) => set({ bus: e.target.checked })} />
            {t("Bus gate: serves remote stands by bus")}
          </label>
        )}
        <label className={a.row}>
          <input
            type="checkbox"
            checked={v.active}
            onChange={(e) => set({ active: e.target.checked })}
            data-testid="res-active"
          />
          {t("On the register (untick to stop the plan using it)")}
        </label>
        <Field label="Why">
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t("new chart, works, a new bridge …")}
          />
        </Field>
        <Button type="submit" variant="primary" disabled={save.isPending || !any} data-testid="res-save">
          {t("Save the change")}
        </Button>
        {!any && <p className={a.dim}>{t("Change a field to save it.")}</p>}
        {bad && (
          <p className={a.bad} role="alert" data-testid="res-refused">
            {bad}
          </p>
        )}
        {done && (
          <p className={a.result} data-outcome="applied" data-testid="res-saved">
            <b>{done.length ? t("Saved. The plan is checked again.") : t("Nothing changed.")}</b>
          </p>
        )}
      </form>
    </Card>
  );
}
